import { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import Brand from './dna/Brand';
import ViewImageModal from './shared/ViewImageModal';
import { getOptimizedImageUrl, handleImageError } from '../utils/imageUtils';
import { authFetch } from '../services/api';

const navItems = [
  { path: '/dashboard', label: 'Vue d’ensemble', icon: 'fa-table-columns' },
  { path: '/dashboard/releases', label: 'Mes sorties', icon: 'fa-compact-disc' },
  { path: '/dashboard/analytics', label: 'Analytiques', icon: 'fa-chart-line', pro: true },
  { path: '/dashboard/community', label: 'Communauté', icon: 'fa-comments' },
];

const DashboardSidebar = ({ artist, profilePic, onProfilePicUpload, onLogout, mobileOpen = false, onCloseMobile = null }) => {
  const isPremium = artist?.subscription_tier === 'premium';
  const location = useLocation();
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  const [quota, setQuota] = useState(null);
  const [quotaLoading, setQuotaLoading] = useState(true);

  const loadQuota = useCallback(async () => {
    try {
      const res = await authFetch('/api/subscriptions/status');
      if (res.ok) {
        const data = await res.json();
        if (data.system_enabled) setQuota(data);
      }
    } catch { /* silence */ }
    finally { setQuotaLoading(false); }
  }, []);

  useEffect(() => { loadQuota(); }, [loadQuota]);

  useEffect(() => {
    const h = () => loadQuota();
    window.addEventListener('passio-upload-success', h);
    return () => window.removeEventListener('passio-upload-success', h);
  }, [loadQuota]);

  useEffect(() => {
    const outside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, []);

  const hasCustomPhoto = profilePic && !profilePic.includes('ui-avatars.com');
  const handleAvatarClick = () => {
    if (hasCustomPhoto) setShowDropdown((p) => !p);
    else fileInputRef.current?.click();
  };
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onProfilePicUpload) onProfilePicUpload(file);
    e.target.value = '';
  };

  const used = quota?.tracks_used_this_month || 0;
  const limit = quota?.limit_this_month || (quota?.tier === 'premium' ? (quota?.premium_track_limit || 30) : (quota?.free_track_limit || 3));
  const pct = Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const remaining = Math.max(0, limit - used);
  const exhausted = !!quota && (quota.tracks_remaining ?? 0) <= 0;
  // Ton positif : on valorise ce qui reste, pas ce qui manque
  const quotaMsg = exhausted
    ? (isPremium ? 'Limite atteinte ce mois — belle productivité !' : 'Quota du mois épuisé — ça repart le mois prochain ✨')
    : (isPremium ? `${remaining} track(s) dispo — profite de ton Premium !` : `${remaining} track(s) dispo ce mois — à toi de jouer !`);

  const initial = (artist?.name?.[0] || 'A').toUpperCase();

  return (
    <>
      <aside className={`dashboard-sidebar ${mobileOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <Brand dark />
          {onCloseMobile && (
            <button className="icon-button" aria-label="Fermer le menu" onClick={onCloseMobile} style={{ color: '#fff' }}>
              <i className="fa-solid fa-xmark" />
            </button>
          )}
        </div>

        <div className="sidebar-label">Studio artiste</div>
        <nav>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={onCloseMobile || undefined}
              className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
            >
              <i className={`fa-solid ${item.icon}`} style={{ width: 18 }} />
              {item.label}
              {item.pro && !isPremium && <span className="pro-mini">PRO</span>}
            </Link>
          ))}
        </nav>

        <div className="sidebar-divider" />

        <Link
          to="/dashboard/settings"
          onClick={onCloseMobile || undefined}
          className={`sidebar-link ${location.pathname === '/dashboard/settings' ? 'active' : ''}`}
        >
          <i className="fa-solid fa-gear" style={{ width: 18 }} />
          Paramètres
        </Link>

        <div className="sidebar-bottom">
          <div className="sidebar-quota">
            <div className="quota-line"><span>Quota mensuel</span><b>{quotaLoading ? '…' : `${used} / ${limit}`}</b></div>
            <div className="quota-bar"><span style={{ width: `${pct}%` }} /></div>
            {!quotaLoading && (
              <small>{quotaMsg}</small>
            )}
            {!isPremium && (
              <Link to="/dashboard/premium" onClick={onCloseMobile || undefined}>
                Passer Premium <i className="fa-solid fa-arrow-right" style={{ fontSize: '.65rem' }} />
              </Link>
            )}
          </div>

          {artist && (
            <div className="sidebar-profile">
              <span
                className="profile-avatar"
                ref={dropdownRef}
                onClick={onProfilePicUpload ? handleAvatarClick : undefined}
                style={{ cursor: onProfilePicUpload ? 'pointer' : 'default', position: 'relative', overflow: 'visible' }}
                title={artist.name}
              >
                {profilePic && !profilePic.includes('ui-avatars.com') ? (
                  <img
                    src={getOptimizedImageUrl(profilePic)}
                    onError={handleImageError}
                    alt={artist.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                  />
                ) : initial}
                <input ref={fileInputRef} type="file" accept="image/*" className="d-none" onChange={handleFileChange} />
                {showDropdown && (
                  <div className="profile-dropdown" style={{
                    position: 'fixed', bottom: '80px', left: '140px', transform: 'translateX(-50%)',
                    background: '#fff', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                    zIndex: 99999, minWidth: '160px', padding: '4px', color: '#333',
                  }}>
                    <button onClick={(e) => { e.stopPropagation(); setShowDropdown(false); setShowPhotoModal(true); }}
                      style={{ display: 'flex', gap: 8, width: '100%', padding: '10px 14px', border: 0, background: 'transparent', cursor: 'pointer', fontSize: '.85rem' }}>
                      <i className="fa-solid fa-eye" /> Voir la photo
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setShowDropdown(false); fileInputRef.current?.click(); }}
                      style={{ display: 'flex', gap: 8, width: '100%', padding: '10px 14px', border: 0, background: 'transparent', cursor: 'pointer', fontSize: '.85rem' }}>
                      <i className="fa-solid fa-camera" /> Changer
                    </button>
                  </div>
                )}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <b style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{artist.name}</b>
                <small style={{ color: isPremium ? '#f2c675' : 'rgba(255,255,255,.5)' }}>{isPremium ? 'Plan Premium' : 'Plan Free'}</small>
              </span>
              <button
                onClick={onLogout}
                title="Déconnexion"
                style={{ background: 'transparent', border: 0, color: 'rgba(255,255,255,.6)', cursor: 'pointer' }}
              >
                <i className="fa-solid fa-right-from-bracket" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {showPhotoModal && ReactDOM.createPortal(
        <ViewImageModal
          image={profilePic?.split('?')[0]}
          onClose={() => setShowPhotoModal(false)}
          onChange={() => { setShowPhotoModal(false); fileInputRef.current?.click(); }}
        />,
        document.body
      )}
    </>
  );
};

export default DashboardSidebar;
